import React from 'react';
import { Box } from 'braid-design-system';
import Loading from '../components/Loading';
import { useToast } from '../hooks/useToast';
import { usePageHeading } from '../contexts/PageHeadingProvider';
import { ItReportTemplateResponse, getAllReportTemplates } from '../services/reportService';
import ReportsViewTable from '../components/Report/ReportViewTable';
import { useTranslation } from 'react-i18next';

const Reports: React.FC = () => {
    const [reports, setReports] = React.useState<ItReportTemplateResponse[] | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const toast = useToast();
    const { setPageHeading } = usePageHeading();
    const { t } = useTranslation();

    const fetchReports = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage(t('page.reports.loading.fetching', { defaultValue: 'Please wait, fetching reports...' }));
            const resp = await getAllReportTemplates();
            setReports(resp);
        } catch (error: any) {
            toast.showError(`Error fetching reports: ${error?.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [t, toast]);

    React.useEffect(() => {
        const handleReportTemplateUpdates = () => {
            fetchReports();
        };

        window.addEventListener('report-templates-updated', handleReportTemplateUpdates);

        return () => {
            window.removeEventListener('report-templates-updated', handleReportTemplateUpdates);
        };
    }, [fetchReports]);

    React.useEffect(() => {
        setPageHeading(t('page.reports.heading', { defaultValue: 'Reports' }));
        fetchReports();
    }, [setPageHeading, fetchReports, t]);


    return (
        <>
            {loading && <Loading message={loadingMessage || t('page.reports.loading.loading', { defaultValue: 'Loading...' })} />}
            <Box padding="medium">
                <ReportsViewTable
                    reportList={reports}
                    setLoadingMessage={setLoadingMessage}
                />
            </Box>
        </>
    );
}

export default Reports;
